// Product variants: option types [{name, values[]}] and one variant per combination
// {id, options:{type:value}, price, compare_at_price, stock, sku, image}. Mirrors backend/variants.py.
export const MAX_TYPES = 3;
export const MAX_VALUES = 20;
export const MAX_VARIANTS = 100;

const lc = (s) => String(s || "").trim().toLowerCase();
export const comboKey = (options, types) => types.map((t) => lc(options[t.name])).join("|");
export const variantLabel = (options, types) => types.map((t) => options[t.name]).filter(Boolean).join(" / ");

/** Every combination of the option values, first type varying slowest. */
export function combos(types) {
  const live = types.filter((t) => t.name.trim() && t.values.length);
  return live.reduce((acc, t) => acc.flatMap((o) => t.values.map((v) => ({ ...o, [t.name.trim()]: v }))), [{}]);
}

/** Rebuild the variant rows after the option types changed, keeping what the vendor already typed for rows that still exist. */
export function syncRows(types, rows, defaults = {}) {
  const live = types.filter((t) => t.name.trim() && t.values.length).map((t) => ({ ...t, name: t.name.trim() }));
  if (!live.length) return [];
  const old = new Map();
  rows.forEach((r) => old.set(live.map((t) => lc(r.options[t.name])).join("|"), r));
  return combos(live).map((options) => {
    const prev = old.get(comboKey(options, live));
    return prev ? { ...prev, options } : { id: "", options, price: defaults.price || "", compare_at_price: "", stock: "0", sku: "", image: "" };
  });
}

/** Row problems for the table: { [index]: message }; and a general message for the option list. */
export function variantErrors(types, rows) {
  const errs = {};
  const skus = new Map();
  rows.forEach((r, i) => {
    const price = Number(r.price);
    if (r.price === "" || !(price > 0)) errs[i] = "Price must be greater than 0.";
    else if (r.compare_at_price !== "" && !(Number(r.compare_at_price) > price)) errs[i] = "Compare-at must be higher than the price.";
    else if (r.stock === "" || !Number.isInteger(Number(r.stock)) || Number(r.stock) < 0) errs[i] = "Stock must be a whole number, 0 or more.";
    else if (r.sku.trim()) {
      const k = lc(r.sku);
      if (skus.has(k)) errs[i] = "SKU is used by another variant.";
      skus.set(k, i);
    }
  });
  return errs;
}

export function typeError(types) {
  const names = new Set();
  for (const t of types) {
    if (!t.name.trim()) return "Give every option a name (like Size).";
    if (!t.values.length) return `Add at least one value for ${t.name}.`;
    if (names.has(lc(t.name))) return "Option names must be different.";
    names.add(lc(t.name));
  }
  return "";
}

export const toFormVariants = (p) => ({
  types: (p.option_types || []).map((t) => ({ name: t.name, values: [...t.values] })),
  rows: (p.variant_items || []).map((v) => ({
    id: v.id, options: { ...v.options }, price: String(v.price), compare_at_price: v.compare_at_price ? String(v.compare_at_price) : "",
    stock: String(v.stock ?? 0), sku: v.sku || "", image: v.image || "",
  })),
});

export const toApiVariants = (types, rows) => ({
  option_types: types.map((t) => ({ name: t.name.trim(), values: t.values })),
  variant_items: rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}), options: r.options, price: Number(r.price), compare_at_price: r.compare_at_price === "" ? null : Number(r.compare_at_price),
    stock: Number(r.stock) || 0, sku: r.sku.trim(), image: r.image || "",
  })),
});

/** The old {name, options[]} variants (no prices) become option types, with prices/stock to be filled in. */
export function fromLegacy(p) {
  const types = (p.variants || []).filter((v) => v.name && v.options?.length).slice(0, MAX_TYPES).map((v) => ({ name: v.name, values: [...v.options] }));
  return { types, rows: syncRows(types, [], { price: String(p.price ?? "") }) };
}

/** Storefront helpers */
export const hasVariants = (p) => (p?.variant_items?.length || 0) > 0;
export const fromPrice = (p) => (hasVariants(p) ? Math.min(...p.variant_items.map((v) => v.price)) : p.price);
export const hasPriceRange = (p) => hasVariants(p) && new Set(p.variant_items.map((v) => v.price)).size > 1;
